// apps/storefront/src/features/cart/useCartSync.ts
// ─────────────────────────────────────────────────────────────────
// Hook que sincroniza el cart local (Zustand) con el backend.
// La lógica vive en cart-sync-engine.ts (testeable sin React); acá solo se
// conecta el store, la API y los eventos `online` / `visibilitychange`.
//
// El hook NO bloquea la UX — las operaciones de sync son background.
// ─────────────────────────────────────────────────────────────────

import { useEffect } from "react";
import { useCartStore } from "./store";
import { fetchServerCart, syncServerCart } from "./api";
import { createCartSyncEngine } from "./cart-sync-engine";
import { peekGuestSessionId } from "@/hooks/useGuestSession";
import { useAuthStore } from "@/store/auth";

// ── Resync channel ──────────────────────────────────────────────────────────
// Permite que código externo (ej: auth flow post-login) fuerce un re-fetch
// del cart desde el server. El hook registra su callback en mount.

let _resyncCallback: (() => void) | null = null;

/**
 * Fuerza que useCartSync haga un nuevo GET /cart y lo una con el carrito
 * local. Llamar después de login/register cuando el backend ya hizo merge.
 */
export function requestCartResync(): void {
  _resyncCallback?.();
}

/** Chequea si tenemos identidad para hacer sync (auth o guest session) */
function canSync(): boolean {
  const hasToken = !!useAuthStore.getState().accessToken;
  const hasGuest = !!peekGuestSessionId();
  return hasToken || hasGuest;
}

function listenWake(onWake: () => void): () => void {
  const onVisibility = () => {
    if (document.visibilityState === "visible") onWake();
  };
  window.addEventListener("online", onWake);
  document.addEventListener("visibilitychange", onVisibility);
  return () => {
    window.removeEventListener("online", onWake);
    document.removeEventListener("visibilitychange", onVisibility);
  };
}

/**
 * Hook que mantiene el cart local sincronizado con el backend.
 * Montar una sola vez en el layout principal del storefront.
 */
export function useCartSync() {
  useEffect(() => {
    const engine = createCartSyncEngine({
      getItems: () => useCartStore.getState().items,
      setItems: (items) => useCartStore.setState({ items }),
      subscribe: (onItemsChange) =>
        useCartStore.subscribe((state, prevState) => {
          if (state.items !== prevState.items) onItemsChange();
        }),
      fetchServerCart,
      syncServerCart,
      canSync,
      listenWake,
    });

    _resyncCallback = engine.resync;
    engine.start();

    return () => {
      engine.stop();
      if (_resyncCallback === engine.resync) {
        _resyncCallback = null;
      }
    };
  }, []);
}
